import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import {
  Sparkles,
  Mail,
  ExternalLink,
  Copy,
  Check,
  Image as ImageIcon,
  Briefcase,
  Globe,
  Users,
  Award,
  Shield,
  Star,
  FileText,
} from "lucide-react";
import {
  getPartners,
  Partner,
  incrementViews,
  incrementWebsiteClicks,
  incrementIntroRequests,
  logActivity,
  trackPartnerEvent,
} from "@/data/partners";
import { categoryConfig, partnerCategories } from "@/constants/partners";
import { getPartnerBadge, getPartnerImage } from "@/helpers/partners";
import { useAuth } from "@/auth/AuthProvider";

const renderRating = (rating: number) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => {
        if (i < fullStars) {
          return (
            <Star key={i} className="h-4 w-4 text-yellow-500 fill-yellow-500" />
          );
        } else if (i === fullStars && hasHalfStar) {
          return (
            <div key={i} className="relative">
              <Star className="h-4 w-4 text-gray-300" />
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: "50%" }}
              >
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              </div>
            </div>
          );
        } else {
          return <Star key={i} className="h-4 w-4 text-gray-300" />;
        }
      })}
      <span className="ml-1 text-sm font-medium">{rating.toFixed(1)}</span>
    </div>
  );
};

const Partners = () => {
  const { user } = useAuth();
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [allPartners, setAllPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const partners = await getPartners();
      setAllPartners(partners);
    } catch (err) {
      console.error("Error fetching partners:", err);
    } finally {
      setLoading(false);
    }
  };
  const handlePartnerClick = (partner: Partner) => {
    setSelectedPartner(partner);
    setIsDialogOpen(true);

    trackPartnerEvent(partner, "view", "increment_views", user);
  };

  const handleWebsiteClick = (partner: Partner) => {
    window.open(partner.website, "_blank");

    trackPartnerEvent(
      partner,
      "website_click",
      "increment_website_clicks",
      user,
    );
  };

  const handleIntroRequest = (partner: Partner) => {
    const subject = `Introduction Request: ${partner.name}`;
    const body = `Hello,\n\nI would like to request an introduction to ${partner.name} (${partner.specialty}).\n\nPlease contact me with more information.\n\nBest regards,`;

    window.open(
      `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      "_blank",
    );

    trackPartnerEvent(
      partner,
      "intro_request",
      "increment_intro_requests",
      user,
    );
  };

  const handleCloseDialog = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsDialogOpen(false);
      setSelectedPartner(null);
      setIsClosing(false);
    }, 300);
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const normalizeCategory = (category: string): string => {
    if (!category) return "";
    return category.toLowerCase().trim();
  };

  const partnersByCategory = partnerCategories.reduce(
    (acc, category) => {
      const categoryPartners = allPartners.filter((partner) => {
        const partnerCategory = normalizeCategory(partner.category);
        const categoryAliases = category.aliases.map((alias) =>
          normalizeCategory(alias),
        );
        return categoryAliases.includes(partnerCategory);
      });

      if (categoryPartners.length > 0) {
        acc[category.id] = categoryPartners;
      }
      return acc;
    },
    {} as Record<string, Partner[]>,
  );

  const unknownCategoryPartners = allPartners.filter((partner) => {
    const partnerCategory = normalizeCategory(partner.category);
    const knownCategory = partnerCategories.some((category) =>
      category.aliases.some(
        (alias) => normalizeCategory(alias) === partnerCategory,
      ),
    );
    return !knownCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen">
        <main className="container py-12">
          <div className="mb-10">
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              Vetted Partners
            </h1>
            <p className="text-muted-foreground text-lg">
              Pre-screened professionals to help bring your hotel to life
            </p>
          </div>

          <div className="mb-10 p-4 rounded-xl bg-primary/5 border border-primary/10 inline-flex items-center gap-3">
            <Badge variant="secondary" className="bg-primary/10 text-primary">
              <Shield className="h-3 w-3 mr-1" />
              Vetted
            </Badge>
            <span className="text-sm text-muted-foreground">
              All partners are personally reviewed by our team of hotel
              operators
            </span>
          </div>

          <div className="space-y-12">
            {partnerCategories.map((category) => {
              const config = categoryConfig[category.id];
              return (
                <section key={category.id}>
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className={`p-2 rounded-lg ${config?.bg || "bg-gray-200"} animate-pulse`}
                    >
                      <div className={`h-5 w-5 ${config?.color || ""}`}></div>
                    </div>
                    <div>
                      <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
                      <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map((index) => (
                      <Card key={index} className="animate-pulse">
                        <div className="h-40 bg-gray-200"></div>
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start mb-3">
                            <div className="w-12 h-12 rounded-xl bg-gray-200"></div>
                            <div className="h-6 w-16 bg-gray-200 rounded"></div>
                          </div>
                          <div className="h-6 bg-gray-200 rounded mb-2"></div>
                          <div className="h-4 bg-gray-200 rounded"></div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="h-4 w-20 bg-gray-200 rounded"></div>
                              <div className="h-6 w-16 bg-gray-200 rounded"></div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="h-4 w-24 bg-gray-200 rounded"></div>
                              <div className="h-4 w-20 bg-gray-200 rounded"></div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <main className="container py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight mb-2">
            Vetted Partners
          </h1>
          <p className="text-muted-foreground text-lg">
            Pre-screened professionals to help bring your hotel to life
          </p>
        </div>

        <div className="mb-10 p-4 rounded-xl bg-primary/5 border border-primary/10 inline-flex items-center gap-3">
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            <Shield className="h-3 w-3 mr-1" />
            Vetted
          </Badge>
          <span className="text-sm text-muted-foreground">
            All partners are personally reviewed by our team of hotel operators
          </span>
        </div>

        <div className="space-y-12">
          {partnerCategories.map((category) => {
            const config = categoryConfig[category.id];
            const partners = partnersByCategory[category.id] || [];

            if (partners.length === 0) return null;

            return (
              <section key={category.id}>
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className={`p-2 rounded-lg ${config?.bg || "bg-gray-200"}`}
                  >
                    <config.icon
                      className={`h-5 w-5 ${config?.color || "text-gray-600"}`}
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">{category.title}</h2>
                    <p className="text-sm text-muted-foreground">
                      {config?.description}
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {partners.map((partner) => {
                    const imageUrl = getPartnerImage(partner);
                    const badge = getPartnerBadge(partner);

                    return (
                      <Card
                        key={partner.id}
                        className="hover:shadow-lg hover:border-primary/20 transition-all duration-200 cursor-pointer group overflow-hidden"
                        onClick={() => handlePartnerClick(partner)}
                      >
                        <div className="h-40 overflow-hidden relative">
                          <img
                            src={imageUrl}
                            alt={partner.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&auto=format&fit=crop";
                            }}
                          />
                          {partner.discount_code && (
                            <div className="absolute top-3 right-3">
                              <Badge className="bg-emerald-500 hover:bg-emerald-600">
                                Special Offer
                              </Badge>
                            </div>
                          )}
                        </div>

                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start mb-3">
                            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                              {partner.main_image ? (
                                <img
                                  src={partner.main_image}
                                  alt={partner.name}
                                  className="w-full h-full object-cover rounded-xl"
                                />
                              ) : (
                                <span className="text-lg font-bold text-muted-foreground group-hover:text-primary transition-colors">
                                  {partner.name.charAt(0)}
                                </span>
                              )}
                            </div>

                            {partner.rating > 0 && renderRating(partner.rating)}
                          </div>

                          <CardTitle className="text-lg">
                            {partner.name}
                          </CardTitle>
                          <CardDescription>{partner.specialty}</CardDescription>
                        </CardHeader>

                        <CardContent>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Globe className="h-3 w-3" />
                                {partner.location}
                              </span>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {badge}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </section>
            );
          })}

          {unknownCategoryPartners.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-gray-200">
                  <Users className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Other Partners</h2>
                  <p className="text-sm text-muted-foreground">
                    Additional service providers for your hotel project
                  </p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {unknownCategoryPartners.map((partner) => {
                  const imageUrl = getPartnerImage(partner);
                  const badge = getPartnerBadge(partner);

                  return (
                    <Card
                      key={partner.id}
                      className="hover:shadow-lg hover:border-primary/20 transition-all duration-200 cursor-pointer group overflow-hidden"
                      onClick={() => handlePartnerClick(partner)}
                    >
                      <div className="h-40 overflow-hidden relative">
                        <img
                          src={imageUrl}
                          alt={partner.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&auto=format&fit=crop";
                          }}
                        />
                        {partner.discount_code && (
                          <div className="absolute top-3 right-3">
                            <Badge className="bg-emerald-500 hover:bg-emerald-600">
                              Special Offer
                            </Badge>
                          </div>
                        )}
                      </div>

                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start mb-3">
                          <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                            {partner.main_image ? (
                              <img
                                src={partner.main_image}
                                alt={partner.name}
                                className="w-full h-full object-cover rounded-xl"
                              />
                            ) : (
                              <span className="text-lg font-bold text-muted-foreground group-hover:text-primary transition-colors">
                                {partner.name.charAt(0)}
                              </span>
                            )}
                          </div>

                          {partner.rating > 0 && renderRating(partner.rating)}
                        </div>

                        <CardTitle className="text-lg">
                          {partner.name}
                        </CardTitle>
                        <CardDescription>{partner.specialty}</CardDescription>
                      </CardHeader>

                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Globe className="h-3 w-3" />
                              {partner.location}
                            </span>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {badge}
                              </Badge>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Briefcase className="h-3 w-3" />
                              {partner.project_count || 0} projects
                            </span>

                            {partner.experience && (
                              <span className="flex items-center gap-1">
                                <Award className="h-3 w-3" />
                                {partner.experience}
                              </span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        <div className="mt-16 text-center p-8 rounded-2xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 backdrop-blur-sm">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-xl font-semibold mb-2">
            Are you a hotel service provider?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Join our vetted partner network and connect with boutique hotel
            developers from around the world.
          </p>
          <Button size="lg" disabled className="gap-2">
            <Sparkles className="h-4 w-4" />
            Apply to Join Network
          </Button>
        </div>
      </main>

      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent
          className={`
      max-w-lg max-h-[95vh] overflow-y-auto
      transition-all duration-300 ease-out
      ${isClosing ? "opacity-0 scale-95 translate-y-4" : "opacity-100 scale-100 translate-y-0"}
    `}
        >
          {selectedPartner && (
            <>
              <DialogHeader className="px-6 pt-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden border border-primary/5">
                    {selectedPartner.main_image ? (
                      <img
                        src={selectedPartner.main_image}
                        alt={selectedPartner.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl font-bold text-primary">
                        {selectedPartner.name.charAt(0)}
                      </span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <DialogTitle className="text-xl truncate">
                      {selectedPartner.name}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground mt-1 truncate">
                      {selectedPartner.specialty}
                    </p>
                  </div>

                  {selectedPartner.rating > 0 && (
                    <div className="flex-shrink-0">
                      {renderRating(selectedPartner.rating)}
                    </div>
                  )}
                </div>
              </DialogHeader>

              <div className="px-6 space-y-6 pb-6 pt-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Globe className="h-4 w-4" />
                    <span>{selectedPartner.location}</span>
                  </div>
                  <Badge variant="secondary">
                    {getPartnerBadge(selectedPartner)}
                  </Badge>
                </div>

                {selectedPartner.description && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">About</p>
                    <DialogDescription className="text-foreground/80 leading-relaxed text-sm">
                      {selectedPartner.description}
                    </DialogDescription>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {selectedPartner.experience && (
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        Experience
                      </p>
                      <p className="text-base font-semibold text-slate-900">
                        {selectedPartner.experience}
                      </p>
                    </div>
                  )}
                  {selectedPartner.project_count > 0 && (
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        Projects
                      </p>
                      <p className="text-base font-semibold text-slate-900">
                        {selectedPartner.project_count}+ Completed
                      </p>
                    </div>
                  )}
                </div>

                {selectedPartner.pdf_url && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4 text-orange-500" />
                      Services & Pricing
                    </p>
                    <div className="flex items-center gap-3 p-3 rounded-xl border bg-orange-50/30 border-orange-100 group">
                      <div className="p-2 bg-white rounded-lg border border-orange-100 shadow-sm group-hover:scale-110 transition-transform duration-200">
                        <FileText className="h-6 w-6 text-orange-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate text-orange-900">
                          Service Presentation
                        </p>
                        <p className="text-[10px] text-orange-700/70 uppercase font-bold tracking-tight">
                          PDF Document • Click to view
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="hover:bg-orange-100 text-orange-600 hover:text-orange-700 h-10 w-10 p-0 rounded-full"
                        onClick={() =>
                          window.open(selectedPartner.pdf_url, "_blank")
                        }
                      >
                        <ExternalLink className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                )}

                {selectedPartner.main_image && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Portfolio Preview</p>
                    <div
                      className="aspect-video rounded-xl bg-muted overflow-hidden border group cursor-pointer relative"
                      onClick={() =>
                        window.open(selectedPartner.main_image!, "_blank")
                      }
                    >
                      <img
                        src={selectedPartner.main_image}
                        alt={`${selectedPartner.name} portfolio`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="bg-white/90 text-black text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                          View Full Image
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t space-y-4">
                  {selectedPartner.website && (
                    <Button
                      variant="outline"
                      className="w-full justify-start h-12 group border-slate-200 hover:border-primary/30"
                      onClick={() => handleWebsiteClick(selectedPartner)}
                    >
                      <ExternalLink className="mr-3 h-4 w-4 text-slate-400 group-hover:text-primary" />
                      <span className="flex-1 text-left">
                        Visit Official Website
                      </span>
                      <span className="text-muted-foreground group-hover:translate-x-1 transition-transform">
                        ↗
                      </span>
                    </Button>
                  )}

                  {selectedPartner.discount_code && (
                    <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100 space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                        <p className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
                          Exclusive Founder's Discount
                        </p>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-lg border border-emerald-200 shadow-sm">
                        <code className="text-sm font-mono font-bold text-emerald-700 px-2">
                          {selectedPartner.discount_code}
                        </code>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            handleCopyCode(selectedPartner.discount_code)
                          }
                          className="h-8 px-3 hover:bg-emerald-50 text-emerald-600"
                        >
                          {copiedCode ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                          <span className="ml-2 text-xs font-bold uppercase">
                            {copiedCode ? "Copied" : "Copy"}
                          </span>
                        </Button>
                      </div>
                    </div>
                  )}

                  <Button
                    className="w-full h-12 bg-[#0047FF] hover:bg-[#0037CC] text-white shadow-md hover:shadow-xl transition-all duration-200 font-bold"
                    onClick={() => handleIntroRequest(selectedPartner)}
                  >
                    <Mail className="mr-3 h-5 w-5" />
                    Request Personal Introduction
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Partners;
